import axios from 'axios';
import {ENDPOINT_URL, SWITCHES_ACL_IPV4_API} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	namedResponseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a Switch IPv4 ACL row identifier.
 *
 * An ACL is identified by its name - the table's delete sends
 * {profileName: row.aclName} - so the name is the identifier.
 *
 * @param {object} item Canvas row
 * @return {*} identifier
 */
function rowId(item) {
	// The list API calls it profileName; the table renames it to
	// aclName for display, so both are accepted here.
	return item.profileName ?? item.aclName ?? item.id;
}

/**
 * Normalize a Canvas Switch IPv4 ACL row.
 * @param {object} item Canvas row
 * @return {object|null} agent candidate
 */
function normalizeRow(item) {
	const id = rowId(item);
	const name = item.profileName ?? item.aclName ?? item.name;
	if (id == null || !name) return null;
	return {
		entity_type: 'switch_ipv4_acl',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Resolve Switch IPv4 ACL candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveSwitchIpv4Acl(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${SWITCHES_ACL_IPV4_API}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return namedResponseRows(
			decodeAgentResponse(data) || {},
			'ipv4AclProfileConfs',
		);
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('switch_ipv4_acl', rows, rowId);
		return {
			candidates: rows.map(normalizeRow).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('switch_ipv4_acl', rows, rowId);
	return {candidates};
}

/**
 * Return a retained Switch IPv4 ACL row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedSwitchIpv4Acl(entityId) {
	return getResolvedEntity('switch_ipv4_acl', entityId);
}
